import React, { useState } from "react";
import { all_routes } from "../../router/all_routes";
import { sendContactForm } from "../../../services/cmsPage.service";
import { toast, ToastContainer } from "react-toastify";
import { Spinner } from "react-bootstrap";
import "./style.scss";

interface FormData {
  name: string;
  email: string;
  phone: string;
  about: string;
}

const ContactUs = () => {
  const route = all_routes;

  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    phone: "",
    about: "",
  });

  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [loading, setLoading] = useState(false);

  const validate = (): boolean => {
    const newErrors: Partial<FormData> = {};

    if (!formData.name.trim()) newErrors.name = "Full Name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }
    if (!formData.phone.trim()) newErrors.phone = "Phone Number is required";
    if (!formData.about.trim()) newErrors.about = "Message is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);
    try {
      const response = await sendContactForm(formData);
      toast.success(response.message || "Message sent successfully!");
      setFormData({ name: "", email: "", phone: "", about: "" }); // Reset
    } catch (err: any) {
      toast.error("An error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };


  return (
    // <div>
    //   <div className="main-wrapper contact-us-page">
    <>

      <section className="banner-section">
        <div className="container">
          <div className="row ">
            <div className="col-md-12 ">
              <div className="main-heading d-flex justify-content-center align-items-center">
                <h1 className="hero-title">Contact Us</h1>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="request-quote-section ">
        <div className="container">
          <div className="row justify-content-center align-items-start g-2">
            {/* Left Form Section */}
            <div className="col-lg-6 mb-4">
              <div className="quote-card shadow-sm rounded-4 overflow-hidden">
                <div className="quote-header text-center text-white py-4 px-3">
                  <h3 className="mb-2">Request a quote now!</h3>
                  <p className="mb-0">
                    Get in touch for a quote, and expert advice from our team of
                    professionals.
                  </p>
                </div>

                <div className="quote-body p-5">
                  <form onSubmit={handleSubmit}>

                    <div className="mb-3">
                      <input
                        type="text"
                        name="name"
                        className={`form-control ${errors.name ? "is-invalid" : ""}`}
                        placeholder="Full Name *"
                        value={formData.name}
                        onChange={handleChange}
                        disabled={loading}
                      />
                      {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                    </div>

                    <div className="mb-3">
                      <input
                        type="email"
                        name="email"
                        className={`form-control ${errors.email ? "is-invalid" : ""}`}
                        placeholder="Email *"
                        value={formData.email}
                        onChange={handleChange}
                        disabled={loading}
                      />
                      {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                    </div>

                    <div className="mb-3">
                      <input
                        type="tel"
                        name="phone"
                        className={`form-control ${errors.phone ? "is-invalid" : ""}`}
                        placeholder="Phone Number *"
                        value={formData.phone}
                        onChange={handleChange}
                        disabled={loading}
                      />
                      {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
                    </div>

                    <div className="mb-3">
                      <textarea
                        name="about"
                        className={`form-control ${errors.about ? "is-invalid" : ""}`}
                        rows={3}
                        placeholder="Tell Us About Your Project! *"
                        value={formData.about}
                        onChange={handleChange}
                        disabled={loading}
                      />
                      {errors.about && <div className="invalid-feedback">{errors.about}</div>}
                    </div>

                    <button
                      type="submit"
                      className="btn btn-warning w-100 text-white fw-semibold py-2 mb-3"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Spinner animation="border" size="sm" /> Sending...
                        </>
                      ) : (
                        "Submit"
                      )}
                    </button>

                    <p className="text-center">
                      * We will never share your personal details with anyone.
                      See our <a href="/privacy-policy">Privacy Policy</a> for more
                      information.
                    </p>
                  </form>

                  {/* Toast Container for showing toast notifications */}
                  <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} />
                </div>
              </div>
            </div>

            {/* Right Info Boxes */}
            <div className="col-lg-5">
              <div className="info-box-section d-flex flex-column gap-4">
                {/* Phone */}
                <div className="info-box p-4 shadow-sm rounded-4 bg-white">
                  <div className="d-flex align-items-start gap-3">
                    <i className="bi bi-telephone fs-3 text-primary"></i>
                    <div>
                      <img src="/assets/img/phone-number.png" alt="Phone Icon" />
                      <h5 className="fw-bold mb-1 mt-4">Phone number</h5>
                      <p className="mb-0 text-muted">
                        <a
                          href="tel:0394341120"
                          className="text-muted text-decoration-none"
                        >
                          (03) 9434 1120
                        </a>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Email */}
                <div className="info-box p-4 shadow-sm rounded-4 bg-white">
                  <div className="d-flex align-items-start gap-3">
                    <i className="bi bi-envelope fs-3 text-primary"></i>
                    <div>
                      <img src="/assets/img/email.png" alt="Email Icon" />
                      <h5 className="fw-bold mb-1 mt-4">Email address</h5>
                      <p className="mb-0 text-muted">
                        <a
                          href="mailto:info@tracknest.com"
                          className="text-muted text-decoration-none"
                        >
                          info@tracknest.com
                        </a>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Location */}
                <div className="info-box p-4 shadow-sm rounded-4 bg-white">
                  <div className="d-flex align-items-start gap-3">
                    <i className="bi bi-geo-alt fs-3 text-primary"></i>
                    <div>
                      <img src="/assets/img/location.png" alt="Location Icon" />
                      <h5 className="fw-bold mb-1 mt-4">Our Location</h5>
                      <p className="mb-0 text-muted">
                        1234 Elm Street Springfield, IL 62704 USA
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

    </>
    //   </div>
    // </div>
  );
};

export default ContactUs;
